import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import Layout from "./components/Layout";
import Home from "./pages/Home";
import {
  PrivacyPolicy,
  TermsOfService,
  RefundPolicy,
  ContactPage,
} from "./pages/LegalPages";
 
import Login from "./pages/Login";
import Register from "./pages/Register";
import { BillingPage } from "./components/BillingPanel";
import ChoosePlan from "./pages/ChoosePlan";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import ClientDetails from "./pages/ClientDetails";
import Letters from "./pages/Letters";
import Disputes from "./pages/Disputes";
import Reports from "./pages/Reports";
import NotesTasks from "./pages/NotesTasks";
import Documents from "./pages/Documents";
import CreditReports from "./pages/CreditReports";
import CaseFiles from "./pages/CaseFiles";
import ClientPortal from "./pages/ClientPortal";
import Settings from "./pages/Settings";
import {
  getCurrentRole,
  getSubscriptionStatus,
} from "./api";
import Employees from "./pages/Employees";
import MyWorkQueue from "./pages/MyWorkQueue";

function ProtectedRoute({
  children,
  roles = [],
}) {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("access_token");

  const [company, setCompany] = useState(() => {
    try {
      return JSON.parse(
        localStorage.getItem("company") || "null"
      );
    } catch {
      return null;
    }
  });

  const [checkingSubscription, setCheckingSubscription] =
    useState(Boolean(token));

  useEffect(() => {
    let cancelled = false;

    async function refreshSubscription() {
      if (!token) {
        setCheckingSubscription(false);
        return;
      }

      try {
        const result = await getSubscriptionStatus();

        if (cancelled) {
          return;
        }

        let cachedCompany = {};

        try {
          cachedCompany = JSON.parse(
            localStorage.getItem("company") || "{}"
          );
        } catch {
          cachedCompany = {};
        }

        const updatedCompany = {
          ...cachedCompany,
          id: result.company_id,
          subscription_status:
            result.subscription_status,
          subscription_plan:
            result.subscription_plan,
        };

        localStorage.setItem(
          "company",
          JSON.stringify(updatedCompany)
        );

        setCompany(updatedCompany);
      } catch (error) {
        if (!cancelled) {
          console.error(
            "Unable to refresh subscription status"
          );
        }
      } finally {
        if (!cancelled) {
          setCheckingSubscription(false);
        }
      }
    }

    refreshSubscription();

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (!token) {
    return (
      <Navigate to="/login" replace />
    );
  }

  if (checkingSubscription) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "20px",
          fontWeight: "700",
        }}
      >
        Checking subscription...
      </div>
    );
  }

  const subscriptionStatus = String(
    company?.subscription_status || ""
  )
    .trim()
    .toLowerCase();

  if (
    subscriptionStatus !== "active" &&
    subscriptionStatus !== "trialing"
  ) {
    return (
      <Navigate to="/choose-plan" replace />
    );
  }

  if (roles.length > 0) {
    const role = getCurrentRole();

    if (!roles.includes(role)) {
      return (
        <Navigate
          to="/dashboard"
          replace
        />
      );
    }
  }

  return children;
}

function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <Layout>
        <Routes>
          <Route
            path="/dashboard"
            element={<Dashboard />}
          />

          <Route
            path="/my-work-queue"
            element={
              <ProtectedRoute>
                <MyWorkQueue />
              </ProtectedRoute>
            }
          />

          <Route
            path="/clients"
            element={<Clients />}
          />

          <Route
            path="/clients/:id"
            element={<ClientDetails />}
          />

          <Route
            path="/letters"
            element={<Letters />}
          />

          <Route
            path="/disputes"
            element={<Disputes />}
          />

          <Route
            path="/reports"
            element={<Reports />}
          />

          <Route
            path="/notes-tasks"
            element={<NotesTasks />}
          />

          <Route
            path="/documents"
            element={<Documents />}
          />

          <Route
            path="/credit-reports"
            element={<CreditReports />}
          />

          <Route
            path="/case-files"
            element={<CaseFiles />}
          />

          <Route
            path="/client-portal"
            element={<ClientPortal />}
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute
                roles={["admin", "manager"]}
              >
                <Settings />
              </ProtectedRoute>
            }
          />

          <Route
            path="/employees"
            element={
              <ProtectedRoute
                roles={["admin", "manager"]}
              >
                <Employees />
              </ProtectedRoute>
            }
          />

          <Route
            path="*"
            element={
              <Navigate
                to="/dashboard"
                replace
              />
            }
          />
        </Routes>
      </Layout>
    </ProtectedRoute>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

export default function App() {
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route
          path="/login"
          element={<Login />}
        />

        <Route
          path="/register"
          element={<Register />}
        />

        <Route path="/billing" element={<BillingPage />} />
        <Route
          path="/choose-plan"
          element={<ChoosePlan />}
        />

        <Route
          path="/"
          element={<Home />}
        />

        <Route
           path="/privacy"
           element={<PrivacyPolicy />}
        />

        <Route
           path="/terms"
           element={<TermsOfService />}
        />

        <Route
           path="/refunds"
           element={<RefundPolicy />}
        />

        <Route
           path="/contact"
           element={<ContactPage />}
        />

        <Route
          path="/*"
          element={<ProtectedLayout />}
        />
      </Routes>
    </Router>
  );
}
